import { useCaps } from "../../CapsProvider";
import { MiniBtn, Row, TextActionInput } from "../../inspector/rows";
import { pickFile } from "../../../../util/download";
import { VariationCategory, useVariations } from "./variationShared";

/**
 * Variation Configurator (v32, iJewel `VariationConfiguratorEditorUiPlugin`) —
 * the editor-side control on the Configurators tab. Load a variation config (by
 * URL or a pasted/uploaded JSON blob), toggle the plugin on/off, then preview
 * each category's material/object options as a swatch grid. Clicking a swatch
 * calls `applyVariation` — the same buyer-facing action the customer grid panel
 * exposes, so the editor previews exactly what shoppers see.
 *
 * All v32 RPCs hang forever on a pre-v32 viewer (the client has no call
 * timeout), so the whole control is gated behind `caps.version >= 32`.
 */
export function VariationConfiguratorControl() {
  const caps = useCaps();
  const hasV32 = caps.version >= 32;
  const { state, applying, busy, reload, apply, setEnabled, clear, loadConfigUrl, setConfig } = useVariations();

  if (!hasV32) {
    return (
      <div className="px-3 py-1.5">
        <span className="text-[11px] text-ed-text-3">Variation Configurator needs viewer contract v32 or newer.</span>
      </div>
    );
  }

  const uploadConfig = () =>
    pickFile(".json", (f) => {
      const reader = new FileReader();
      reader.onload = () => { if (typeof reader.result === "string") setConfig(reader.result); };
      reader.readAsText(f);
    });

  const categories = state?.categories ?? [];
  const enabled = state?.enabled ?? false;

  return (
    <div className="flex flex-col gap-2 px-3 py-1.5">
      <Row label="Config URL" stacked>
        <TextActionInput placeholder="variations.json URL…" action="Load" onSubmit={loadConfigUrl} />
      </Row>
      <div className="flex flex-wrap items-center gap-1.5">
        <MiniBtn icon="upload_file" onClick={uploadConfig}>Upload JSON</MiniBtn>
        <MiniBtn icon={enabled ? "toggle_on" : "toggle_off"} active={enabled} onClick={() => setEnabled(!enabled)}>
          {enabled ? "Enabled" : "Disabled"}
        </MiniBtn>
        <MiniBtn icon="refresh" onClick={reload}>Refresh</MiniBtn>
        {categories.length ? <MiniBtn icon="delete" onClick={clear}>Clear</MiniBtn> : null}
      </div>

      {busy ? (
        <span className="text-[11px] text-ed-text-3">Loading config…</span>
      ) : categories.length === 0 ? (
        <span className="text-[11px] text-ed-text-3">
          Load a variation config to expose switchable material / object options to customers.
        </span>
      ) : (
        <div className="flex flex-col gap-3 pt-0.5">
          {categories.map((cat) => (
            <VariationCategory
              key={cat.name}
              cat={cat}
              tileSize={40}
              applyingItem={applying[cat.name] ?? null}
              onApply={(i) => apply(cat.name, i)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
